<script setup lang="ts">
// Settings › Your storage (docs/STORAGE.md §2.5, §11): S3 buckets I attached and hrth-nodes I
// paired, when the server lets members, opening my nodes to other people, and other people's
// nodes opened to me: where libraries of my own live.
// Files are encrypted on devices before they go in, so neither the server nor the storage
// can read them.
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { describeError } from '../api/http'
import type { MyStorage, StorageLocation } from '../api/types'
import { useSession } from '../stores/session'
import { installCommand, nodeCommand } from '../lib/memberStorage'
import { serverBase } from '../platform'
import { formatBytes } from '../lib/pattern'
import { checkBucket, keyRevoked, type AccessKey, type BucketRecord } from '../lib/sealedStorage'
import BucketAddForm from './BucketAddForm.vue'
import NodeShareForm from './NodeShareForm.vue'
import ThisComputerNode from './ThisComputerNode.vue'
import { nodeStatus, nodeSupported, type DesktopNode } from '../lib/desktopNode'
import PromptModal from './PromptModal.vue'
import { askRoom, roomText, type NodeAnswer } from '../lib/nodeShare'

const session = useSession()
const mine = ref<MyStorage | null>(null)
const adding = ref(false)
const busy = ref(false)
/** Sealed buckets' records, opened on this device (null: its key is on another device). */
const records = ref<Record<string, BucketRecord | null>>({})
interface Row {
  loc: StorageLocation
  name: string
  where: string
  /** The server still holds this bucket's key (from before sealing): offer to seal it. */
  unsealed: boolean
  readOnly: boolean
  /** Old keys it replaced that may still work at the provider. */
  retired: AccessKey[]
  node: boolean
}
/** Whether a node is connected: as the server last said over the socket, else as listed. */
const nodeOnline = (loc: StorageLocation) => session.nodeOnline?.[loc.id] ?? !!loc.online
const rows = computed<Row[]>(() =>
  (mine.value?.locations ?? []).map((loc) => {
    if (!loc.sealed) {
      return {
        loc,
        name: loc.name,
        where: `${[loc.bucket, loc.prefix].filter(Boolean).join('/')} · ${loc.endpoint || 'AWS'}`,
        unsealed: true,
        readOnly: false,
        retired: [],
        node: false,
      }
    }
    const rec = records.value[loc.id]
    if (loc.kind === 'node') {
      return {
        loc,
        name: rec?.name ?? 'Node',
        where: `Node · ${!loc.linked ? 'waiting to be paired' : nodeOnline(loc) ? 'online' : 'offline'}`,
        unsealed: false,
        readOnly: false,
        retired: [],
        node: true,
      }
    }
    return {
      loc,
      name: rec?.name ?? 'Bucket',
      where: rec ? `${[rec.bucket, rec.prefix].filter(Boolean).join('/')} · ${rec.endpoint || 'AWS'}` : 'Its key is on another of your devices: open hrth there',
      unsealed: false,
      readOnly: !!rec && !rec.write,
      retired: rec?.retired ?? [],
      node: false,
    }
  }),
)

/** The bucket being renamed. */
const renaming = ref<Row | null>(null)
async function rename(name: string): Promise<void> {
  const r = renaming.value!
  if (r.loc.sealed) await session.engine!.renameBucket(r.loc, name.trim())
  else await session.api.renameMyStorage(r.loc.id, name.trim())
  await load()
}

/** Sealing a bucket the server still holds the key of: a read-only key, and the write key kept or not. */
const sealing = ref<Row | null>(null)
const seal = reactive({ readId: '', readSecret: '', keepWrite: true })
const seal_ = () =>
  run(async () => {
    const r = sealing.value!
    await session.engine!.sealBucket(r.loc.id, { id: seal.readId.trim(), secret: seal.readSecret }, seal.keepWrite ? 'current' : null)
    sealing.value = null
    Object.assign(seal, { readId: '', readSecret: '', keepWrite: true })
    await load()
  }, 'Sealed: the server no longer holds this bucket’s key')

/**
 * Replacing a sealed bucket's keys (when people who had them lose access): new ones made at
 * the provider, checked, then the old ones checked until they're deleted there.
 */
const rekeying = ref<Row | null>(null)
const rekey = reactive({ readId: '', readSecret: '', newWrite: false, writeId: '', writeSecret: '' })
const rekeyReady = computed(
  () => rekey.readId.trim() && rekey.readSecret && (!rekey.newWrite || (rekey.writeId.trim() && rekey.writeSecret)),
)
const rekey_ = () =>
  run(async () => {
    const r = rekeying.value!
    const rec = records.value[r.loc.id]!
    const read = { id: rekey.readId.trim(), secret: rekey.readSecret }
    const write = rekey.newWrite ? { id: rekey.writeId.trim(), secret: rekey.writeSecret } : rec.write ? 'current' : null
    await checkBucket(session.api, rec, read, write === 'current' ? rec.write : (write ?? undefined))
    const loc = await session.engine!.replaceBucketKeys(r.loc, read, write)
    rekeying.value = null
    Object.assign(rekey, { readId: '', readSecret: '', newWrite: false, writeId: '', writeSecret: '' })
    await load()
    if (await checkRetired(loc)) session.toast('Keys replaced: now delete the old ones at your provider')
    else session.toast('Keys replaced')
  })

/** Checks a bucket's old keys and forgets the revoked ones: how many still work. */
async function checkRetired(loc: StorageLocation): Promise<number> {
  const rec = records.value[loc.id]
  if (!rec?.retired?.length) return 0
  const revoked: string[] = []
  for (const k of rec.retired) if (await keyRevoked(session.api, rec, k)) revoked.push(k.id)
  if (revoked.length) {
    await session.engine!.forgetRetiredKeys(loc, revoked)
    await load()
  }
  return rec.retired.length - revoked.length
}
const recheck = (r: Row) =>
  run(async () => {
    const left = await checkRetired(r.loc)
    if (!left) session.toast('Old keys revoked')
    else session.toast(`${left === 1 ? 'The old key still works' : `${left} old keys still work`}: delete ${left === 1 ? 'it' : 'them'} at your provider`, 'error')
  })

/** The node being opened to other people. */
const sharing = ref<Row | null>(null)
async function shared(): Promise<void> {
  sharing.value = null
  await load()
}
/** How many people, spaces and roles a node of mine is open to. */
const openCount = (loc: StorageLocation) =>
  (loc.open_to?.users.length ?? 0) + (loc.open_to?.spaces.length ?? 0) + (loc.open_to?.roles.length ?? 0)

/** What other people's nodes open to me say about themselves and my room there. */
const answers = ref<Record<string, NodeAnswer | 'offline'>>({})
async function askOpenNodes(): Promise<void> {
  const out: Record<string, NodeAnswer | 'offline'> = {}
  for (const n of mine.value?.open_nodes ?? []) {
    try {
      out[n.id] = await askRoom(session.api, n.id)
    } catch {
      out[n.id] = 'offline'
    }
  }
  answers.value = out
}
const ownerName = (user: string) => session.profiles.get(user)?.display_name ?? user

const full = computed(() => !!mine.value && mine.value.max_locations > 0 && mine.value.locations.length >= mine.value.max_locations)
const of = (n: number, max: number) => (max ? `${n} of ${max}` : `${n}`)

/** In the desktop app: this computer's node, if it can have one (HRTH-103). */
const desktop = ref(false)
const here = ref<DesktopNode | null>(null)
/** This computer is a node of someone else's (another account or server): say so. */
const hereElsewhere = computed(() => !!here.value?.paired && !mine.value?.locations.some((l) => l.id === here.value?.location))

async function load(): Promise<void> {
  try {
    if (desktop.value) here.value = await nodeStatus().catch(() => null)
    mine.value = await session.api.myStorage()
    const opened: Record<string, BucketRecord | null> = {}
    for (const l of mine.value.locations) if (l.sealed) opened[l.id] = (await session.engine?.bucket(l)) ?? null
    records.value = opened
    void askOpenNodes()
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function run(fn: () => Promise<void>, ok?: string): Promise<void> {
  busy.value = true
  try {
    await fn()
    if (ok) session.toast(ok)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

async function added(): Promise<void> {
  adding.value = false
  await load()
  session.toast('Bucket attached: its keys stay on your devices')
}

/** Checks a bucket: a sealed one from this device through the relay, an old one by the server. */
const check = (r: Row) =>
  run(async () => {
    const rec = records.value[r.loc.id]
    if (!r.loc.sealed) await session.api.checkMyStorage(r.loc.id)
    else if (rec) await checkBucket(session.api, rec, rec.read, rec.write)
    else throw new Error('Its key is on another of your devices')
  }, `${r.name} works`)
const remove = (r: Row) =>
  run(async () => {
    if (!confirm(`Remove ${r.name}? Files already in the bucket are left there.`)) return
    await session.api.removeMyStorage(r.loc.id)
    await load()
  })

/**
 * Adding a node: its location (name sealed for my devices) and a one-time pairing link that
 * carries the location's key past the server. The node is never a device: it can't read my
 * conversations. The list refreshes until the node has claimed the link.
 */
const node = reactive({ open: false, name: 'Home server', link: '', expires: 0, here: false })
let nodeTimer: ReturnType<typeof setInterval> | undefined
function stopWaiting(): void {
  if (nodeTimer) clearInterval(nodeTimer)
  nodeTimer = undefined
}
function closeNode(): void {
  stopWaiting()
  Object.assign(node, { open: false, link: '', expires: 0, here: false })
}
const linkNode = () =>
  run(async () => {
    const { location, link } = await session.engine!.pairNode(node.name.trim())
    Object.assign(node, { link, expires: Date.now() + 15 * 60_000 })
    await load()
    stopWaiting()
    nodeTimer = setInterval(async () => {
      if (Date.now() > node.expires) return closeNode()
      await load()
      if (mine.value?.locations.some((l) => l.id === location.id && l.linked)) {
        closeNode()
        session.toast('Node paired: its folders appear in the Library as it shares them')
      }
    }, 3000)
  })
const command = computed(() => (node.link ? nodeCommand(node.link) : ''))
const install = installCommand(serverBase() || globalThis.location.origin)
const copy = (text: string) =>
  navigator.clipboard.writeText(text).then(
    () => session.toast('Copied'),
    () => session.toast('Couldn’t copy: select it instead', 'error'),
  )

onMounted(async () => {
  desktop.value = await nodeSupported()
  await load()
})
/** Set up from Add a node: once paired, it's managed from its own row. */
async function hereChanged(): Promise<void> {
  await load()
  if (here.value?.paired) closeNode()
}
onUnmounted(stopWaiting)
</script>

<template>
  <section v-if="mine && (mine.allowed || mine.open_nodes?.length)" class="space-y-3" data-testid="my-storage">
    <div class="label">Your storage</div>
    <template v-if="mine.allowed">
    <p class="text-xs text-text-muted">
      Attach an S3 bucket in your own account (Backblaze B2, Cloudflare R2, Wasabi, AWS, your own Garage or MinIO) and
      create libraries in it from the Library, or add a node: a computer of yours (a NAS, a Raspberry Pi, a spare PC)
      that serves its folders as libraries. They're yours: you share them and choose who adds files. Everything is
      encrypted on devices before it's stored or sent.
    </p>
    <p class="mono text-text-muted" data-testid="my-storage-allowance">
      {{ of(mine.locations.length, mine.max_locations) }} buckets and nodes · {{ of(mine.libraries, mine.max_libraries) }} libraries
    </p>
    <div v-for="r in rows" :key="r.loc.id" class="border-b border-border py-2 text-sm" :data-testid="`my-storage-${r.name}`">
      <div class="flex items-center gap-3">
        <span class="min-w-0 flex-1 leading-tight">
          <span class="block truncate">
            {{ r.name }}<span v-if="r.readOnly" class="mono ml-1.5 text-text-muted uppercase" data-testid="my-storage-read-only">read-only</span>
          </span>
          <span class="mono block truncate" :class="r.node && r.loc.linked && !nodeOnline(r.loc) ? 'text-signal' : 'text-text-muted'" :data-testid="r.node ? 'my-storage-node-status' : undefined">{{ r.where }} · {{ r.loc.libraries }} {{ r.loc.libraries === 1 ? 'library' : 'libraries' }}</span>
        </span>
        <button type="button" class="btn-ghost px-2 text-xs" :disabled="busy || (!!r.loc.sealed && !records[r.loc.id])" data-testid="my-storage-rename" @click="renaming = r">Rename</button>
        <button v-if="!r.node" type="button" class="btn-ghost px-2 text-xs" :disabled="busy" data-testid="my-storage-check" @click="check(r)">Check</button>
        <button
          v-if="r.node && r.loc.linked"
          type="button"
          class="btn-ghost px-2 text-xs"
          :disabled="busy || !records[r.loc.id]"
          :title="records[r.loc.id] ? 'Let people keep libraries on it' : 'Its key is on another of your devices'"
          data-testid="my-storage-share"
          @click="sharing = sharing?.loc.id === r.loc.id ? null : r"
        >
          Share{{ openCount(r.loc) ? ` · ${openCount(r.loc)}` : '' }}
        </button>
        <button
          v-if="!r.node && r.loc.sealed && records[r.loc.id]"
          type="button"
          class="btn-ghost px-2 text-xs"
          :disabled="busy"
          data-testid="my-storage-rekey"
          @click="rekeying = rekeying?.loc.id === r.loc.id ? null : r"
        >
          Keys
        </button>
        <button
          type="button"
          class="btn-ghost px-2 text-xs hover:text-signal"
          :disabled="busy || r.loc.libraries > 0"
          :title="r.loc.libraries > 0 ? 'Delete its libraries first' : undefined"
          :aria-label="`Remove ${r.name}`"
          data-testid="my-storage-remove"
          @click="remove(r)"
        >
          ✕
        </button>
      </div>
      <p v-if="r.unsealed" class="mono mt-1 text-text-muted">
        The server holds this bucket's key.
        <button type="button" class="underline hover:text-text-primary" data-testid="my-storage-seal" @click="sealing = r">Seal it</button>
        so only your devices do.
      </p>
      <p v-if="r.retired.length" class="mono mt-1 text-signal" data-testid="my-storage-retired">
        {{ r.retired.length === 1 ? 'An old key' : `${r.retired.length} old keys` }} ({{ r.retired.map((k) => k.id).join(', ') }})
        may still work: delete {{ r.retired.length === 1 ? 'it' : 'them' }} at your provider.
        <button type="button" class="underline hover:text-text-primary" :disabled="busy" data-testid="my-storage-retired-check" @click="recheck(r)">Check again</button>
      </p>
      <ThisComputerNode v-if="desktop && here?.paired && here.location === r.loc.id" class="mt-2" :node="here" :loc="r.loc" @changed="load" />
      <NodeShareForm v-if="sharing?.loc.id === r.loc.id" :loc="r.loc" :name="r.name" @saved="shared" @cancel="sharing = null" />
      <form v-if="rekeying?.loc.id === r.loc.id" class="mt-2 space-y-2 border border-border p-3" data-testid="rekey-form" @submit.prevent="rekey_">
        <p class="mono text-text-muted">
          Everyone who could read this bucket's libraries has its read key{{ records[r.loc.id]?.write ? ', and uploaders its upload key' : '' }}.
          After removing people, make a new key at your provider, paste it here, then delete the old one there.
        </p>
        <div class="grid gap-2 md:grid-cols-2">
          <input v-model="rekey.readId" class="input" autocomplete="off" placeholder="New read-only key ID" aria-label="New read-only key ID" data-testid="rekey-read-id" />
          <input v-model="rekey.readSecret" type="password" class="input" autocomplete="off" placeholder="Secret" aria-label="New read-only key secret" data-testid="rekey-read-secret" />
        </div>
        <label v-if="records[r.loc.id]?.write" class="flex cursor-pointer items-start gap-2 text-xs">
          <input v-model="rekey.newWrite" type="checkbox" data-testid="rekey-new-write" />
          <span>Replace the upload key too</span>
        </label>
        <div v-if="rekey.newWrite" class="grid gap-2 md:grid-cols-2">
          <input v-model="rekey.writeId" class="input" autocomplete="off" placeholder="New read-write key ID" aria-label="New read-write key ID" data-testid="rekey-write-id" />
          <input v-model="rekey.writeSecret" type="password" class="input" autocomplete="off" placeholder="Secret" aria-label="New read-write key secret" data-testid="rekey-write-secret" />
        </div>
        <div class="flex justify-end gap-2">
          <button type="button" class="btn-ghost" @click="rekeying = null">Cancel</button>
          <button class="btn-primary" :disabled="busy || !rekeyReady" data-testid="rekey-save">{{ busy ? 'Checking…' : 'Check and replace' }}</button>
        </div>
      </form>
      <form v-if="sealing?.loc.id === r.loc.id" class="mt-2 space-y-2 border border-border p-3" data-testid="seal-form" @submit.prevent="seal_">
        <p class="mono text-text-muted">A read-only key for this bucket, for the people you share its libraries with.</p>
        <div class="grid gap-2 md:grid-cols-2">
          <input v-model="seal.readId" class="input" autocomplete="off" placeholder="Read-only key ID" aria-label="Read-only key ID" data-testid="seal-read-id" />
          <input v-model="seal.readSecret" type="password" class="input" autocomplete="off" placeholder="Secret" aria-label="Read-only key secret" data-testid="seal-read-secret" />
        </div>
        <label class="flex cursor-pointer items-start gap-2 text-xs">
          <input v-model="seal.keepWrite" type="checkbox" data-testid="seal-keep-write" />
          <span>Keep the current key for uploading (off: read-only storage)</span>
        </label>
        <div class="flex justify-end gap-2">
          <button type="button" class="btn-ghost" @click="sealing = null">Cancel</button>
          <button class="btn-primary" :disabled="busy || !seal.readId.trim() || !seal.readSecret" data-testid="seal-save">Seal</button>
        </div>
      </form>
    </div>
    <template v-if="!adding && !node.open">
      <div v-if="!full" class="flex flex-wrap gap-2">
        <button type="button" class="btn-ghost text-xs" data-testid="storage-add-open" @click="adding = true">+ Attach a bucket</button>
        <button type="button" class="btn-ghost text-xs" data-testid="node-add-open" @click="node.open = true">+ Add a node</button>
      </div>
      <p v-else class="mono text-text-muted">That's as many buckets and nodes as this server allows each person.</p>
    </template>
    <BucketAddForm v-else-if="adding" @added="added" @cancel="adding = false" />
    <form v-else class="space-y-2 border border-border p-3" data-testid="node-add" @submit.prevent="linkNode">
      <p class="mono text-text-muted">
        A node serves folders on one of your computers as libraries, encrypting each file as it sends it: a NAS, a
        desktop, or this server's own machine. It connects out to this server, so it needs no open port. It isn't one
        of your devices: it can't read your conversations.
      </p>
      <!-- The desktop app can make this computer the node, with no terminal. -->
      <div v-if="desktop && !command && !here?.paired" class="flex flex-wrap gap-2" role="radiogroup" aria-label="Which computer">
        <button type="button" :class="node.here ? 'btn-primary' : 'btn-ghost'" class="text-xs" data-testid="node-this-computer" @click="node.here = true">This computer</button>
        <button type="button" :class="!node.here ? 'btn-primary' : 'btn-ghost'" class="text-xs" data-testid="node-another-computer" @click="node.here = false">Another computer</button>
      </div>
      <p v-if="desktop && hereElsewhere" class="mono text-text-muted" data-testid="node-here-elsewhere">
        This computer is already a node of {{ here?.server }}: add another computer here.
      </p>
      <ThisComputerNode v-if="desktop && node.here && here && !here.paired" :node="here" @changed="hereChanged" @cancel="closeNode" />
      <template v-else-if="!command">
        <input v-model="node.name" class="input" maxlength="48" placeholder="What to call it" aria-label="Node name" data-testid="node-name" />
        <div class="flex justify-end gap-2">
          <button type="button" class="btn-ghost" @click="closeNode">Cancel</button>
          <button class="btn-primary" :disabled="busy || !node.name.trim()" data-testid="node-link">Get the command</button>
        </div>
      </template>
      <template v-else>
        <div class="label">1. Install hrth there (Linux or macOS; skip if it has it)</div>
        <div class="flex items-start gap-2">
          <code class="mono min-w-0 flex-1 border border-border p-2 break-all select-all" data-testid="node-install">{{ install }}</code>
          <button type="button" class="btn-ghost px-2 text-xs" @click="copy(install)">Copy</button>
        </div>
        <div class="label">2. Run this in the folder to share</div>
        <div class="flex items-start gap-2">
          <code class="mono min-w-0 flex-1 border border-border p-2 break-all select-all" data-testid="node-command">{{ command }}</code>
          <button type="button" class="btn-ghost px-2 text-xs" @click="copy(command)">Copy</button>
        </div>
        <p class="mono text-text-muted" data-testid="node-waiting">Waiting for the node… The link works once, for 15 minutes. Keep it private: it lets a computer serve libraries as yours.</p>
        <div class="flex justify-end">
          <button type="button" class="btn-ghost" @click="closeNode">Close</button>
        </div>
      </template>
    </form>
    </template>

    <template v-if="mine.open_nodes?.length">
      <div class="label mt-5">Opened to you</div>
      <p class="text-xs text-text-muted">
        Other people's nodes you may keep libraries on, up to the limit they set. Libraries you create there are
        yours; their computer keeps the files, encrypted, and can't read them.
      </p>
      <div v-for="n in mine.open_nodes" :key="n.id" class="flex items-center gap-3 border-b border-border py-2 text-sm" data-testid="open-node">
        <span class="min-w-0 flex-1 leading-tight">
          <span class="block truncate">{{ (answers[n.id] as NodeAnswer | undefined)?.name ?? 'Node' }} · {{ ownerName(n.owner) }}'s</span>
          <span class="mono block truncate text-text-muted" data-testid="open-node-room">
            <template v-if="answers[n.id] === 'offline' || !n.online">offline</template>
            <template v-else-if="answers[n.id]">{{ formatBytes((answers[n.id] as NodeAnswer).used) }} used · {{ roomText(answers[n.id] as NodeAnswer) }}</template>
            <template v-else>asking…</template>
            · {{ n.libraries }} {{ n.libraries === 1 ? 'library' : 'libraries' }} of yours
          </span>
        </span>
      </div>
    </template>

    <PromptModal
      v-if="renaming"
      title="Rename storage"
      label="Name"
      :initial="renaming.name"
      submit-label="Rename"
      :maxlength="48"
      :action="rename"
      @close="renaming = null"
    />
  </section>
</template>
